import React, { useState, useEffect } from "react";
import house from "../../img/house1.png";
import fullLogo1 from "../../img/fulllogo-transparent-nobuffer-1.png";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { handleLogin, handleSuperuserLogin } from "../../API/api";
import { SetLoginCredentials } from "../../utils/SetCredentials";
import { useDispatch, useSelector } from "react-redux";
import { LoginSuccess } from "../../Redux/userSlice";
import { setUserData } from "../../Analytics/Analytics";
import { getFirstAccessiblePath } from "../../utils/pageAccess";

const LoginPage = ({ isSuperuserLogin = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLogged, user } = useSelector((state) => state.user);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [errors, setErrors] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    platform: "email",
    product_name: "revenue",
  });

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (!["email", "password"].includes(name)) return;

    setFormData((prevData) => ({
      ...prevData,
      [name]: name === "email" ? value.toLowerCase() : value,
    }));
  };

  const handleValidation = () => {
    let newErrors = {};
    let hasErrors = false;
    if (formData.email === "" || formData.email === null) {
      newErrors.email = "Email is Required";
      hasErrors = true;
    }
    if (formData.password === "" || formData.password === null) {
      newErrors.password = "Password is Required";
      hasErrors = true;
    }

    setErrors(newErrors);
    return hasErrors;
  };

  useEffect(() => {
    if (isLogged) {
      navigate(getFirstAccessiblePath(user), {
        replace: true,
      });
    }
  }, [isLogged, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let validateResponse = handleValidation();
    if (validateResponse) {
      toast.error("Invalid Credentials");
      return null;
    }

    try {
      setLoading(true);
      const response = isSuperuserLogin
        ? await handleSuperuserLogin(formData)
        : await handleLogin(formData);
      if (response.status >= 200 && response.status <= 209) {
        const data = response.data;
        SetLoginCredentials(data.access, data.refresh);
        dispatch(LoginSuccess(data));
        setUserData(data.first_name, data.organization_name);
        toast.success(isSuperuserLogin ? "Welcome Superuser" : "Welcome");
        navigate(getFirstAccessiblePath(data));
      } else {
        toast.error(
          response.response.data.detail || response.response.data.error
        );
      }
    } catch (error) {
      toast.error("Error in login. Check Credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 my-10 md:my-0">
      <div
        className="w-full gap-[1.4rem] rounded-lg flex flex-col md:flex-row overflow-hidden 
                 max-w-[90%] sm:max-w-4xl md:max-w-[90%] lg:max-w-[80%] xl:max-w-[70%] 2xl:max-w-[70%]"
      >
        {/* Left Section */}
        <div className="w-[100%] md:w-[43%] p-[8%] md:p-[3%] pt-[6%] bg-halfWhite rounded-[25px]">
          <div className=" w-full h-[61px] top-[41px] left-10 flex items-center justify-center ">
            <img className="w-10 h-[61px]" alt="Logo" src={fullLogo1} />
            <span className="ml-2 font-semibold text-2xl mt-6">LOFTx.ai</span>
          </div>
          <form
            onSubmit={handleSubmit}
            className=" pt-4 lg:px-[6%] xl:px-[10%] mt-[10%]"
          >
            {isSuperuserLogin && (
              <div className="mb-5 flex items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-primary/12 to-sky-100 border border-primary/15 px-4 py-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-primary/70 font-semibold">
                    Restricted Access
                  </p>
                  <p className="text-sm text-primary font-semibold mt-0.5">
                    Superuser Login
                  </p>
                </div>
                <span className="inline-flex items-center rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
                  Superuser
                </span>
              </div>
            )}
            <div>
              <label className="block text-[13px] pb-2 text-gray-600 [font-family:'Poppins',Helvetica] tracking-normal font-semibold">
                EMAIL ADDRESS<span className="text-red">*</span> &nbsp;{" "}
              </label>
              <input
                type="email"
                className={`flex justify-end w-full pl-[1.5rem] pr-4 pb-[0.65rem] pt-[0.5rem] bg-white  rounded-[25px] focus:outline-none focus:shadow-lg shadow-md focus:border-primary placeholder-gray-400 ${
                  errors.email
                    ? "border border-red"
                    : "border border-transparent"
                }`}
                placeholder="example@gmail.com"
                name="email"
                onChange={handleInputChange}
              />
              <span className="text-red/70 font-normal text-xs ml-4">
                {errors.email}
              </span>
            </div>

            <div className="relative">
              <label className="block font-semibold text-[13px]  pb-2 text-gray-600 [font-family:'Poppins',Helvetica] tracking-tight mt-4 ">
                PASSWORD<span className="text-red">*</span> &nbsp;{" "}
              </label>
              <input
                type={passwordVisible ? "text" : "password"}
                className={`flex justify-end w-full pl-[1.5rem] pr-[3.5rem]  pb-[0.65rem] pt-[0.5rem] bg-white  rounded-[25px] focus:outline-none focus:shadow-lg focus:border-primary shadow-md placeholder-gray-400 ${
                  errors.password
                    ? "border border-red"
                    : "border border-transparent"
                }`}
                placeholder="∗∗∗∗∗∗∗∗∗∗"
                name="password"
                onChange={handleInputChange}
              />
              <button
                type="button"
                className="absolute top-[70%] bottom-[26%] right-[5%] flex items-center"
                onClick={togglePasswordVisibility}
              >
                {passwordVisible ? (
                  <svg
                    className="w-6 h-6 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 2L22 22"
                      stroke="#000000"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6.71277 6.7226C3.66479 8.79527 2 12 2 12C2 12 5.63636 19 12 19C14.0503 19 15.8174 18.2734 17.2711 17.2884M11 5.05822C11.3254 5.02013 11.6588 5 12 5C18.3636 5 22 12 22 12C22 12 21.3082 13.3317 20 14.8335"
                      stroke="#000000"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14 14.2362C13.4692 14.7112 12.7684 15.0001 12 15.0001C10.3431 15.0001 9 13.657 9 12.0001C9 11.1764 9.33193 10.4303 9.86932 9.88818"
                      stroke="#000000"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12"
                      stroke="#000000"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 12C1 12 5 20 12 20C19 20 23 12 23 12"
                      stroke="#000000"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="#000000"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
            <span className="text-red/70 font-normal text-xs ml-4">
              {errors.password}
            </span>

            <div className=" xl:text-[14px] lg:text-[12px] md:text-[12px] xl:mt-10 lg:mt-8 mt-8 w-full flex justify-between md:gap-0 gap-3">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="form-checkbox text-indigo-600"
                />
                <span className="ml-2 [font-family:'Poppins',Helvetica] font-[500]">
                  Remember me
                </span>
              </label>
              <div className="text-primary [font-family:'Poppins',Helvetica]">
                <Link to="/forgot-password">Forgot Password?</Link>
              </div>
            </div>

            <button
              type="submit"
              className={`w-full text-[12px] font-semibold text-white p-[4%] rounded-[25px] xl:mt-[15%] lg:mt-[10%] mt-[8%] [font-family:'Poppins',Helvetica] ${
                loading ? "bg-grey" : "bg-black"
              }`}
              disabled={loading}
            >
              {loading
                ? "Logging In..."
                : isSuperuserLogin
                  ? "Log In as Superuser"
                  : "Log In"}
            </button>

            {/* <p className="text-[16px] text-center mt-7 [font-family:'Poppins',Helvetica] tracking-normal font-[500]">
              Don’t have an account yet? <Link to="/signup" className="text-[#FF914D]"> New Account</Link> </p> */}
          </form>
        </div>

        {/* Right Section */}
        <div className="w-full md:w-[57%] sm:min-h-[45vh] min-h-[50vh] md:min-h-[55vh] lg:min-h-[75vh] rounded-[30px] flex relative justify-center  bg-gradient-to-r from-[rgb(180.56,230.46,246.5)] to-[#4682B4]">
          <div className="text-left md:pr-[4%] lg:pr-[8%] xl:pr-[19.5%] pt-[10%] ">
            <h2 className="md:text-[20px] text-[18px] [font-family:'Poppins',Helvetica] text-gray-700 mb-2 font-medium tracking-normal">
              {isSuperuserLogin ? "WELCOME SUPERUSER" : "WELCOME TO LOFTx.ai"}
            </h2>
            <h2 className="xl:text-[40px] lg:text-[32px] text-[22px] font-bold [font-family:'Poppins',Helvetica]  lg:leading-[1.1] leading-[1.3] tracking-normal">
              Compare
              <br /> Your Property
            </h2>
            <p className="mt-2.5 xl:text-[18px] text-[16px] font-[500] text-[#6b6b6b] [font-family:'Poppins',Helvetica] tracking-normal">
              Discover homes that match your lifestyle.
            </p>
          </div>
          <img
            src={house}
            alt="Property comparison"
            className="mt-8 mx-auto absolute bottom-[-18px] w-full"
          />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
